/**
 * Labstep
 *
 * @module services/ketcher/utils
 * @desc Utility functions for Ketcher service
 */

import type { Ketcher } from 'ketcher-core';
import ketcherService from './index';

/**
 * Get SVG from SMILES using Ketcher
 * @param smiles SMILES string
 * @param ketcher Ketcher instance (optional, will try to get from global)
 * @returns Promise<string> SVG string
 */
export const getSvgFromSmiles = async (
  smiles: string,
  ketcher?: Ketcher,
): Promise<string> => {
  let ketcherInstance = ketcher;

  // If no ketcher instance provided, try to get from global window
  if (!ketcherInstance && typeof window !== 'undefined') {
    // Try to get from iframe
    const ketcherFrame = document.querySelector(
      'iframe[title="ketcherFrame"]',
    ) as HTMLIFrameElement;
    if (ketcherFrame?.contentWindow?.ketcher) {
      ketcherInstance = ketcherFrame.contentWindow.ketcher;
    }
  }

  if (!ketcherInstance) {
    throw new Error(
      'Ketcher instance not available. Make sure Ketcher is loaded.',
    );
  }

  // Initialize the service if not already done
  if (!ketcherService.isReady()) {
    ketcherService.initialize(ketcherInstance);
  }

  // Generate SVG from SMILES
  return await ketcherService.getStructure(smiles, 'svg', 'smiles');
};

/**
 * Initialize Ketcher service with a Ketcher instance
 * @param ketcher Ketcher instance
 */
export const initializeKetcher = (ketcher: Ketcher): void => {
  ketcherService.initialize(ketcher);
};

/**
 * Check if Ketcher service is ready
 */
export const isKetcherReady = (): boolean => {
  return ketcherService.isReady();
};

export default {
  getSvgFromSmiles,
  initializeKetcher,
  isKetcherReady,
};
